import { Controller } from "@hotwired/stimulus"

// Keeps an agent-notification checkbox unavailable while its destination field
// is empty, on the server-rendered order forms.
//
// Why this exists: LeadOrder validates that send_text has an agent_phone and
// send_email an agent_email, and switches the flag off on write when it does
// not (#9228). Rejecting at save time is a poor place to learn this, so the control is disabled at the point of the
// mistake instead, with the reason stated rather than left to be guessed.
//
// Generic on purpose: the same pairing appears on both the lead-order form and
// the realtime thank-you card, with different field names and markup in each.
// Wire it by targets rather than reimplementing it twice.
//
//   <div data-controller="notification-gate">
//     <input data-notification-gate-target="phone"
//            data-action="input->notification-gate#sync">
//     <input type="checkbox" data-notification-gate-target="smsFlag">
//   </div>
//
// Any target may be absent — a form that only offers email still works.
export default class extends Controller {
  static targets = ["phone", "email", "smsFlag", "emailFlag"]

  static values = {
    smsReason: { type: String, default: "Add a display phone to enable SMS notifications" },
    emailReason: { type: String, default: "Add a display email to enable email notifications" },
  }

  // Re-entrancy guard: #setChecked dispatches `change`, which this controller
  // now listens for, so without it a forced uncheck would re-enter sync.
  #syncing = false

  connect() {
    // Listen on the FLAGS as well as the destinations, so the gate re-asserts
    // itself after anything else writes to a checkbox it manages.
    //
    // This is defence against a whole class of caller rather than a specific
    // one. Several pre-existing paths set .checked directly and some do it
    // ASYNCHRONOUSLY, after every controller has connected —
    // the retired per-type order form's loadProfileDefaults resolved a fetch
    // and then checked the Email box, on a card whose email field always
    // rendered blank. Fixing each caller as it is found has been a losing game; making
    // the gate self-healing ends it, and any caller we have not thought of is
    // covered too.
    //
    // Bound listeners rather than a data-action in the views: this way it
    // cannot be forgotten on the next surface that adopts the controller.
    this.boundSync = () => this.sync()
    this.#flags().forEach((flag) => flag.addEventListener("change", this.boundSync))
    this.sync()
  }

  disconnect() {
    this.#flags().forEach((flag) => flag.removeEventListener("change", this.boundSync))
  }

  #flags() {
    return [
      this.hasSmsFlagTarget ? this.smsFlagTarget : null,
      this.hasEmailFlagTarget ? this.emailFlagTarget : null,
    ].filter(Boolean)
  }

  sync() {
    if (this.#syncing) return

    this.#syncing = true
    try {
      this.#syncAll()
    } finally {
      this.#syncing = false
    }
  }

  #syncAll() {
    this.#apply(this.hasPhoneTarget ? this.phoneTarget : null,
                this.hasSmsFlagTarget ? this.smsFlagTarget : null,
                this.smsReasonValue)
    this.#apply(this.hasEmailTarget ? this.emailTarget : null,
                this.hasEmailFlagTarget ? this.emailFlagTarget : null,
                this.emailReasonValue)
  }

  // A missing destination field means the form does not offer that channel's
  // address at all, so there is nothing to gate on — leave the flag alone
  // rather than disabling a control the user has no way to satisfy.
  #apply(destination, flag, reason) {
    if (!flag || !destination) return

    const filled = destination.value.trim() !== ""
    const ours = flag.dataset.notificationGateDisabled === "true"

    // Never touch a flag disabled for another reason (aged order, per-field
    // permissions). Ownership is tracked with a data flag, and the guard has to
    // cover the SIDE EFFECTS as well as the disabling — an earlier version
    // guarded only the re-enable, so it still force-unchecked and retitled a
    // server-disabled checkbox. On an aged order whose SMS is legitimately on,
    // that displayed the preference as off: a lie about persisted state, on a
    // control the user cannot correct because it is disabled.
    if (flag.disabled && !ours) return

    if (filled) {
      if (ours) {
        flag.disabled = false
        delete flag.dataset.notificationGateDisabled
        flag.removeAttribute("title")
        // Restore whatever we forced off. Without this the gate silently
        // DOWNGRADES a default: on lead_orders/new, send_text defaults to
        // checked and agent_phone defaults to current_user.phone, so an agent
        // with no phone on file gets SMS unchecked by connect() before they
        // have touched anything. Typing a phone — satisfying exactly what the
        // gate asked for — would re-enable the box but leave it unchecked, and
        // they would lose a notification they had by default without noticing.
        if (flag.dataset.notificationGateWasChecked === "true") {
          delete flag.dataset.notificationGateWasChecked
          this.#setChecked(flag, true)
        }
      }
      return
    }

    flag.disabled = true
    flag.dataset.notificationGateDisabled = "true"
    // Unchecking matters as much as disabling: a disabled checkbox is not
    // submitted, so leaving it visibly checked shows a state the save will not
    // persist — the same mismatch the React panel had. Remember the prior state
    // so re-enabling can put it back.
    if (flag.checked) flag.dataset.notificationGateWasChecked = "true"
    this.#setChecked(flag, false)
    flag.setAttribute("title", reason)
  }

  // Assigning .checked fires nothing, so sibling controllers never see it —
  // the same trap #11827-era fixes closed in the retired per-type order form's
  // setters, which applied there and not here. It bites concretely: the realtime
  // thank-you card wires change->notification-pdf-link#syncEmail on the email
  // box to force "Incl PDF" off whenever Email goes off. That card's email
  // field always starts empty, so connect() unchecks Email for any profile with
  // notify_agent_email and notify_agent_include_pdf both on — and without a
  // dispatch, Incl PDF stays checked. Nothing at the model layer ties
  // send_lead_pdf to send_email, so that saves as a real inconsistency.
  //
  // Only dispatched on an actual transition; a no-op assignment must not
  // manufacture an event.
  #setChecked(flag, value) {
    if (flag.checked === value) return

    flag.checked = value
    flag.dispatchEvent(new Event("change", { bubbles: true }))
  }
}
